import type { BackendTradeStatistics, TradeStatistics } from "../types/trade";
import { getEvidenceDocumentRoot } from "./reportEvidencePointer";

function toFiniteNumber(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function num(row: Record<string, unknown>, ...keys: string[]): number {
  for (const k of keys) {
    const v = toFiniteNumber(row[k]);
    if (v !== undefined) return v;
  }
  return 0;
}

function numOrNull(row: Record<string, unknown>, ...keys: string[]): number | null {
  for (const k of keys) {
    const v = toFiniteNumber(row[k]);
    if (v !== undefined) return v;
  }
  return null;
}

/**
 * True when `o` looks like a `/trades/statistics`-shaped aggregate (not a condition bracket row).
 */
export function looksLikeTradeStatisticsAggregate(o: unknown): o is Record<string, unknown> {
  if (!o || typeof o !== "object" || Array.isArray(o)) return false;
  const r = o as Record<string, unknown>;
  const totalTrades = toFiniteNumber(r.total_trades) ?? toFiniteNumber(r.totalTrades);
  if (totalTrades === undefined) return false;

  const open = toFiniteNumber(r.open_trades) ?? toFiniteNumber(r.openTrades);
  const closed = toFiniteNumber(r.closed_trades) ?? toFiniteNumber(r.closedTrades);
  const winRate = toFiniteNumber(r.win_rate) ?? toFiniteNumber(r.winRate);
  const totalPnl = toFiniteNumber(r.total_pnl) ?? toFiniteNumber(r.totalPnL);
  const winning = toFiniteNumber(r.winning_trades) ?? toFiniteNumber(r.winningTrades);

  const hasCohort =
    open !== undefined || closed !== undefined || winning !== undefined || winRate !== undefined || totalPnl !== undefined;
  return hasCohort;
}

function coerceBackendTradeStatistics(row: Record<string, unknown>): BackendTradeStatistics {
  return {
    total_trades: Math.round(num(row, "total_trades", "totalTrades")),
    open_trades: Math.round(num(row, "open_trades", "openTrades")),
    closed_trades: Math.round(num(row, "closed_trades", "closedTrades")),
    winning_trades: Math.round(num(row, "winning_trades", "winningTrades")),
    losing_trades: Math.round(num(row, "losing_trades", "losingTrades")),
    win_rate: num(row, "win_rate", "winRate"),
    total_pnl: num(row, "total_pnl", "totalPnL"),
    average_win: num(row, "average_win", "averageWin"),
    average_loss: num(row, "average_loss", "averageLoss"),
    largest_win: num(row, "largest_win", "largestWin"),
    largest_loss: num(row, "largest_loss", "largestLoss"),
    best_trade: (row.best_trade as BackendTradeStatistics["best_trade"]) ?? null,
    worst_trade: (row.worst_trade as BackendTradeStatistics["worst_trade"]) ?? null,
    total_return_percent: num(row, "total_return_percent", "totalReturnPercent"),
    average_return_percent: num(row, "average_return_percent", "averageReturnPercent"),
    most_traded_symbol:
      typeof row.most_traded_symbol === "string"
        ? row.most_traded_symbol
        : typeof row.mostTradedSymbol === "string"
          ? row.mostTradedSymbol
          : null,
    most_traded_symbol_count: (() => {
      const v = toFiniteNumber(row.most_traded_symbol_count) ?? toFiniteNumber(row.mostTradedSymbolCount);
      return v !== undefined ? Math.round(v) : null;
    })(),
    current_win_streak: Math.round(num(row, "current_win_streak", "currentWinStreak")),
    current_loss_streak: Math.round(num(row, "current_loss_streak", "currentLossStreak")),
    longest_win_streak: Math.round(num(row, "longest_win_streak", "longestWinStreak")),
    longest_loss_streak: Math.round(num(row, "longest_loss_streak", "longestLossStreak")),
    portfolio_value: numOrNull(row, "portfolio_value", "portfolioValue"),
    portfolio_change_percent: numOrNull(row, "portfolio_change_percent", "portfolioChangePercent"),
    win_rate_change_percent: numOrNull(row, "win_rate_change_percent", "winRateChangePercent"),
    average_daily_gain_loss: numOrNull(row, "average_daily_gain_loss", "averageDailyGainLoss"),
    average_hold_time_scratch: numOrNull(row, "average_hold_time_scratch", "averageHoldTimeScratch"),
    average_hold_time_winning: numOrNull(row, "average_hold_time_winning", "averageHoldTimeWinning"),
    average_hold_time_losing: numOrNull(row, "average_hold_time_losing", "averageHoldTimeLosing"),
    trade_pnl_standard_deviation: numOrNull(row, "trade_pnl_standard_deviation", "tradePnlStandardDeviation"),
    kelly_percentage: numOrNull(row, "kelly_percentage", "kellyPercentage"),
    profit_factor: numOrNull(row, "profit_factor", "profitFactor"),
    total_commissions: numOrNull(row, "total_commissions", "totalCommissions"),
    total_fees: numOrNull(row, "total_fees", "totalFees"),
    average_daily_volume: numOrNull(row, "average_daily_volume", "averageDailyVolume"),
    system_quality_number: numOrNull(row, "system_quality_number", "systemQualityNumber"),
    average_per_trade_pnl: numOrNull(
      row,
      "average_per_trade_pnl",
      "averagePerTradePnl",
      "expectancy"
    ),
    average_per_unit_pnl: numOrNull(row, "average_per_unit_pnl", "averagePerUnitPnl"),
    probability_of_random_chance: numOrNull(row, "probability_of_random_chance", "probabilityOfRandomChance"),
    average_return_change_percent: numOrNull(row, "average_return_change_percent", "averageReturnChangePercent"),
    pnl_change_percent: numOrNull(row, "pnl_change_percent", "pnlChangePercent"),
    total_trades_change_percent: numOrNull(row, "total_trades_change_percent", "totalTradesChangePercent"),
    closed_trades_change_percent: numOrNull(row, "closed_trades_change_percent", "closedTradesChangePercent"),
    average_return_vs_previous_percent: numOrNull(row, "average_return_vs_previous_percent", "averageReturnVsPreviousPercent"),
    pnl_vs_previous_percent: numOrNull(row, "pnl_vs_previous_percent", "pnlVsPreviousPercent"),
    total_trades_vs_previous_percent: numOrNull(row, "total_trades_vs_previous_percent", "totalTradesVsPreviousPercent"),
    closed_trades_vs_previous_percent: numOrNull(row, "closed_trades_vs_previous_percent", "closedTradesVsPreviousPercent"),
  };
}

function pickFromDoc(doc: Record<string, unknown>): Record<string, unknown> | null {
  const directKeys = ["trade_statistics", "tradeStatistics", "statistics", "window_statistics", "windowStatistics"];
  for (const k of directKeys) {
    const v = doc[k];
    if (looksLikeTradeStatisticsAggregate(v)) return v;
  }

  const stats = doc.stats;
  if (looksLikeTradeStatisticsAggregate(stats)) return stats as Record<string, unknown>;

  if (stats && typeof stats === "object" && !Array.isArray(stats)) {
    const s = stats as Record<string, unknown>;
    const nestedKeys = [
      "trade_statistics",
      "tradeStatistics",
      "summary",
      "overview",
      "totals",
      "aggregated",
      "bundle_statistics",
      "bundleStatistics",
    ];
    for (const k of nestedKeys) {
      const v = s[k];
      if (looksLikeTradeStatisticsAggregate(v)) return v as Record<string, unknown>;
    }
    for (const v of Object.values(s)) {
      if (looksLikeTradeStatisticsAggregate(v)) return v as Record<string, unknown>;
    }
  }

  return null;
}

/**
 * Reads a `/trades/statistics`-shaped object from the report `input_snapshot` (trade bundle or wrapped `result`).
 * Returns null when the snapshot does not include an aggregate block we recognize.
 */
export function extractReportWindowTradeStatistics(snapshot: unknown): BackendTradeStatistics | null {
  const root = getEvidenceDocumentRoot(snapshot);
  if (!root || typeof root !== "object" || Array.isArray(root)) return null;
  const picked = pickFromDoc(root as Record<string, unknown>);
  if (!picked) return null;
  return coerceBackendTradeStatistics(picked);
}

export function backendTradeStatisticsToFrontend(b: BackendTradeStatistics): TradeStatistics {
  return {
    totalTrades: b.total_trades,
    openTrades: b.open_trades,
    closedTrades: b.closed_trades,
    winningTrades: b.winning_trades,
    losingTrades: b.losing_trades,
    winRate: b.win_rate,
    totalPnL: b.total_pnl,
    averageWin: b.average_win,
    averageLoss: b.average_loss,
    largestWin: b.largest_win,
    largestLoss: b.largest_loss,
    totalReturnPercent: b.total_return_percent,
    averageReturnPercent: b.average_return_percent,
    mostTradedSymbol: b.most_traded_symbol ?? undefined,
    mostTradedSymbolCount: b.most_traded_symbol_count ?? undefined,
  };
}
